import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Spin, Empty, Input, Alert, Button, message, Pagination } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { api, http } from '../api/client';
import { PersonalReportDetail } from './PersonalReportDetail';
import {
  countVisibleDirectoryItems,
  DEPT2_EMPTY,
  dept1HasDept2Children,
  groupByOrgThenDept,
  isHiddenRootOrg,
  normalizeOrgLabel,
  sortedDept1Groups,
  type DirectoryItem,
  type DirectoryResp,
  type OrgPanel,
} from './personalReportTypes';
import './reportEditorial.css';

function itemBelongsToOrg(item: { company: string }, rootOrg: string): boolean {
  return normalizeOrgLabel(item.company) === rootOrg;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error ?? new Error('读取 PDF 字节失败'));
    fr.onload = () => {
      const v = fr.result;
      if (typeof v !== 'string') {
        reject(new Error('FileReader 未返回 data URL 字符串'));
        return;
      }
      resolve(v);
    };
    fr.readAsDataURL(blob);
  });
}

const PAGE_SIZE = 20;

function dept1ExpandKey(rootOrg: string, dept1: string): string {
  return `${rootOrg}::${dept1}`;
}

function toggleSetMember(prev: Set<string>, key: string): Set<string> {
  const next = new Set(prev);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

export function PersonalReportDirectory({ batchId }: { batchId?: number }) {
  const [keyword, setKeyword] = useState('');
  const [selectedEmp, setSelectedEmp] = useState<DirectoryItem | null>(null);
  const [filterRootOrg, setFilterRootOrg] = useState<string | undefined>(undefined);
  const [filterDept1, setFilterDept1] = useState<string | undefined>(undefined);
  const [filterDept2, setFilterDept2] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [hpOnly, setHpOnly] = useState(false);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(() => new Set());
  const [expandedDept1s, setExpandedDept1s] = useState<Set<string>>(() => new Set());

  const { data, isLoading } = useQuery<DirectoryResp>({
    queryKey: ['reports-directory'],
    queryFn: () => api.get('/reports/directory'),
  });

  const { data: hpData = [], isLoading: hpLoading } = useQuery<
    Array<{ evaluateeId: number; isHighPotential: boolean }>
  >({
    queryKey: ['cockpit-hp', batchId],
    queryFn: () => api.get('/admin/high-potential/results', { params: { batchId } }),
    enabled: !!batchId,
    staleTime: 60_000,
  });

  const hpEmpIds = useMemo(() => {
    if (!hpOnly) return null;
    if (hpLoading) return null;
    return new Set(
      hpData.filter((r) => r.isHighPotential).map((r) => r.evaluateeId),
    );
  }, [hpOnly, hpData, hpLoading]);

  const keywordFiltered: DirectoryItem[] = useMemo(() => {
    const all = (data?.items ?? []).filter((i) => !isHiddenRootOrg(i.company));
    const kw = keyword.trim();
    if (!kw) return all;
    const lc = kw.toLowerCase();
    return all.filter(
      (i) =>
        i.name.includes(kw) ||
        (i.empNo || '').toLowerCase().includes(lc) ||
        (i.position || '').toLowerCase().includes(lc) ||
        (i.dept1 || '').includes(kw) ||
        (i.dept2 || '').includes(kw) ||
        (i.company || '').includes(kw),
    );
  }, [data, keyword]);

  const orgPanels: OrgPanel[] = useMemo(
    () => groupByOrgThenDept(keywordFiltered),
    [keywordFiltered],
  );

  const filtered: DirectoryItem[] = useMemo(() => {
    let list = keywordFiltered;
    if (filterRootOrg) {
      list = list.filter((i) => itemBelongsToOrg(i, filterRootOrg));
    }
    if (filterDept1) {
      list = list.filter((i) => (i.dept1 || '').trim() === filterDept1);
    }
    if (filterDept2 !== undefined) {
      if (filterDept2 === DEPT2_EMPTY) {
        list = list.filter((i) => !(i.dept2 || '').trim());
      } else {
        list = list.filter((i) => (i.dept2 || '').trim() === filterDept2);
      }
    }
    if (hpEmpIds) {
      list = list.filter((i) => hpEmpIds.has(i.empId));
    }
    return list.sort((a, b) => {
      const da = `${a.dept1}${a.dept2}${a.name}`;
      const db = `${b.dept1}${b.dept2}${b.name}`;
      return da.localeCompare(db, 'zh-CN');
    });
  }, [keywordFiltered, filterRootOrg, filterDept1, filterDept2, hpEmpIds]);

  const paginatedFiltered = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  const clearTreeFilters = () => {
    setFilterRootOrg(undefined);
    setFilterDept1(undefined);
    setFilterDept2(undefined);
    setCurrentPage(1);
  };

  const selectOrg = (org: string) => {
    if (filterRootOrg === org && !filterDept1 && filterDept2 === undefined) {
      clearTreeFilters();
      return;
    }
    setFilterRootOrg(org);
    setFilterDept1(undefined);
    setFilterDept2(undefined);
    setCurrentPage(1);
  };

  const selectDept1 = (org: string, dept1: string) => {
    if (filterRootOrg === org && filterDept1 === dept1 && filterDept2 === undefined) {
      setFilterDept1(undefined);
      setFilterDept2(undefined);
      setCurrentPage(1);
      return;
    }
    setFilterRootOrg(org);
    setFilterDept1(dept1);
    setFilterDept2(undefined);
    setCurrentPage(1);
  };

  const selectDept2 = (org: string, dept1: string, dept2: string) => {
    if (filterRootOrg === org && filterDept1 === dept1 && filterDept2 === dept2) {
      setFilterDept2(undefined);
      setCurrentPage(1);
      return;
    }
    setFilterRootOrg(org);
    setFilterDept1(dept1);
    setFilterDept2(dept2);
    setCurrentPage(1);
  };

  const toggleOrgExpand = (org: string, e: MouseEvent) => {
    e.stopPropagation();
    setExpandedOrgs((prev) => toggleSetMember(prev, org));
  };

  const toggleDept1Expand = (org: string, dept1: string, e: MouseEvent) => {
    e.stopPropagation();
    setExpandedDept1s((prev) => toggleSetMember(prev, dept1ExpandKey(org, dept1)));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [keyword, filterRootOrg, filterDept1, filterDept2, hpOnly]);

  useEffect(() => {
    if (!filterRootOrg) return;
    setExpandedOrgs((prev) => {
      if (prev.has(filterRootOrg)) return prev;
      const next = new Set(prev);
      next.add(filterRootOrg);
      return next;
    });
  }, [filterRootOrg]);

  useEffect(() => {
    if (!filterRootOrg || !filterDept1) return;
    const key = dept1ExpandKey(filterRootOrg, filterDept1);
    setExpandedDept1s((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, [filterRootOrg, filterDept1]);

  useEffect(() => {
    if (!filterRootOrg) return;
    if (!orgPanels.some((p) => p.rootOrg === filterRootOrg)) {
      clearTreeFilters();
    }
  }, [filterRootOrg, orgPanels]);

  useEffect(() => {
    if (!filterDept1 || !filterRootOrg) return;
    const panel = orgPanels.find((p) => p.rootOrg === filterRootOrg);
    if (!panel?.grouped.has(filterDept1)) {
      setFilterDept1(undefined);
      setFilterDept2(undefined);
    }
  }, [filterDept1, filterRootOrg, orgPanels]);

  useEffect(() => {
    if (filterDept2 === undefined || !filterRootOrg || !filterDept1) return;
    const panel = orgPanels.find((p) => p.rootOrg === filterRootOrg);
    const group = panel?.grouped.get(filterDept1);
    if (!group) {
      setFilterDept2(undefined);
      return;
    }
    if (filterDept2 === DEPT2_EMPTY) {
      if (!(group.dept2Map.get('')?.length ?? 0)) setFilterDept2(undefined);
      return;
    }
    if (!(group.dept2Map.get(filterDept2)?.length ?? 0)) {
      setFilterDept2(undefined);
    }
  }, [filterDept2, filterRootOrg, filterDept1, orgPanels]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filtered.length, currentPage]);

  const downloadPdf = async (emp: DirectoryItem) => {
    if (!batchId) {
      message.warning('请先选择批次');
      return;
    }
    const hide = message.loading(`正在生成 ${emp.name} 的 PDF…`, 0);
    try {
      const r = await http.get(`/reports/personal/${emp.empId}.pdf`, {
        params: { batchId },
        responseType: 'blob',
      });
      const blob: Blob = r.data;
      if (blob.size === 0) throw new Error('服务器返回了空响应');
      const ct = (r.headers?.['content-type'] || blob.type || '').toString();
      if (ct.includes('json')) {
        const text = await blob.text();
        let msg = text;
        try {
          const j = JSON.parse(text);
          msg = j?.message || j?.error || text;
        } catch {
          /* keep */
        }
        throw new Error(`服务端返回 JSON 而非 PDF：${msg}`);
      }
      const head = await blob.slice(0, 5).text();
      if (head !== '%PDF-') {
        throw new Error(
          `响应不是合法 PDF（前 5 字节="${head}"，content-type="${ct}"，size=${blob.size}）`,
        );
      }
      const dataUrl = await blobToDataUrl(blob);
      const fileName = `人才盘点-${emp.name}-${emp.empNo}.pdf`;
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = fileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      message.success(`已下载：${fileName}（${(blob.size / 1024).toFixed(1)} KB）`, 6);
    } catch (e: unknown) {
      const err = e as { message?: string; response?: { status?: number; data?: Blob } };
      let detail = err?.message ?? '';
      if (err?.response?.data instanceof Blob) {
        try {
          const t = await err.response.data.text();
          try {
            const j = JSON.parse(t);
            detail = j?.message || j?.error || t;
          } catch {
            detail = t || detail;
          }
        } catch {
          /* keep */
        }
      }
      message.error(`下载失败：${err?.response?.status ?? ''} ${detail}`, 8);
    } finally {
      hide();
    }
  };

  const selectedIndex = selectedEmp
    ? filtered.findIndex((e) => e.empId === selectedEmp.empId)
    : -1;

  const isAllActive = !filterRootOrg && !filterDept1 && filterDept2 === undefined;

  if (selectedEmp) {
    return (
      <PersonalReportDetail
        batchId={batchId}
        emp={selectedEmp}
        onBack={() => setSelectedEmp(null)}
        onPrev={
          selectedIndex > 0
            ? () => setSelectedEmp(filtered[selectedIndex - 1])
            : undefined
        }
        onNext={
          selectedIndex >= 0 && selectedIndex < filtered.length - 1
            ? () => setSelectedEmp(filtered[selectedIndex + 1])
            : undefined
        }
        hasPrev={selectedIndex > 0}
        hasNext={selectedIndex >= 0 && selectedIndex < filtered.length - 1}
        onDownloadPdf={downloadPdf}
      />
    );
  }

  return (
    <div className="personal-report-directory">
      {!batchId && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="请先在顶部选择批次，下载/查看报告需要批次上下文"
        />
      )}

      <div className="personal-report-directory-body">
        <aside className="dept-rail">
          <div className="dept-rail-title">组织</div>
          <nav className="dept-rail-list">
            <button
              type="button"
              className={`dept-rail-item${isAllActive ? ' is-active' : ''}`}
              onClick={clearTreeFilters}
            >
              <span className="dept-rail-item-label" title="全部部门">
                全部部门
              </span>
              <span className="dept-rail-item-count">{keywordFiltered.length}</span>
            </button>

            {orgPanels.map((panel) => {
              const orgExpanded = expandedOrgs.has(panel.rootOrg);
              const orgActive =
                filterRootOrg === panel.rootOrg &&
                !filterDept1 &&
                filterDept2 === undefined;
              const hasDeptChildren = panel.grouped.size > 0;

              return (
                <div key={panel.rootOrg} className="dept-rail-branch">
                  <button
                    type="button"
                    className={`dept-rail-item dept-rail-item--org${orgActive ? ' is-active' : ''}`}
                    onClick={() => selectOrg(panel.rootOrg)}
                  >
                    {hasDeptChildren ? (
                      <span
                        role="button"
                        tabIndex={-1}
                        className={`dept-rail-chevron${orgExpanded ? ' is-expanded' : ''}`}
                        onClick={(e) => toggleOrgExpand(panel.rootOrg, e)}
                        aria-label={orgExpanded ? '收起' : '展开'}
                      >
                        <CaretRightOutlined />
                      </span>
                    ) : (
                      <span className="dept-rail-chevron dept-rail-chevron--spacer" />
                    )}
                    <span className="dept-rail-item-label" title={panel.rootOrg}>
                      {panel.rootOrg}
                    </span>
                    <span className="dept-rail-item-count">{panel.totalInOrg}</span>
                  </button>

                  {orgExpanded && hasDeptChildren
                    ? sortedDept1Groups(panel.grouped).map((g) => {
                        const d1Key = dept1ExpandKey(panel.rootOrg, g.dept1);
                        const d1Expanded = expandedDept1s.has(d1Key);
                        const hasAnyDept2 = dept1HasDept2Children(g);
                        const dept2Keys = Array.from(g.dept2Map.keys());
                        const d1Active =
                          filterRootOrg === panel.rootOrg &&
                          filterDept1 === g.dept1 &&
                          filterDept2 === undefined;

                        const dept2Rows = hasAnyDept2
                          ? [
                              ...dept2Keys
                                .filter((d) => !!d)
                                .sort((a, b) => a.localeCompare(b, 'zh-CN'))
                                .map((d2) => ({
                                  key: d2,
                                  label: d2,
                                  count: g.dept2Map.get(d2)?.length ?? 0,
                                  value: d2,
                                })),
                              ...(g.dept2Map.get('')?.length
                                ? [
                                    {
                                      key: DEPT2_EMPTY,
                                      label: '（无二级部门）',
                                      count: g.dept2Map.get('')!.length,
                                      value: DEPT2_EMPTY,
                                    },
                                  ]
                                : []),
                            ]
                          : [];

                        return (
                          <div key={d1Key} className="dept-rail-branch dept-rail-branch--dept1">
                            <button
                              type="button"
                              className={`dept-rail-item dept-rail-item--dept1${d1Active ? ' is-active' : ''}`}
                              onClick={() => selectDept1(panel.rootOrg, g.dept1)}
                            >
                              {dept2Rows.length > 0 ? (
                                <span
                                  role="button"
                                  tabIndex={-1}
                                  className={`dept-rail-chevron${d1Expanded ? ' is-expanded' : ''}`}
                                  onClick={(e) =>
                                    toggleDept1Expand(panel.rootOrg, g.dept1, e)
                                  }
                                  aria-label={d1Expanded ? '收起' : '展开'}
                                >
                                  <CaretRightOutlined />
                                </span>
                              ) : (
                                <span className="dept-rail-chevron dept-rail-chevron--spacer" />
                              )}
                              <span className="dept-rail-item-label" title={g.dept1}>
                                {g.dept1}
                              </span>
                              <span className="dept-rail-item-count">{g.total}</span>
                            </button>

                            {d1Expanded && dept2Rows.length > 0
                              ? dept2Rows.map((row) => {
                                  const d2Active =
                                    filterRootOrg === panel.rootOrg &&
                                    filterDept1 === g.dept1 &&
                                    filterDept2 === row.value;
                                  return (
                                    <button
                                      key={`${d1Key}::${row.key}`}
                                      type="button"
                                      className={`dept-rail-item dept-rail-item--dept2${d2Active ? ' is-active' : ''}`}
                                      onClick={() =>
                                        selectDept2(panel.rootOrg, g.dept1, row.value)
                                      }
                                    >
                                      <span className="dept-rail-chevron dept-rail-chevron--spacer" />
                                      <span className="dept-rail-item-label" title={row.label}>
                                        {row.label}
                                      </span>
                                      <span className="dept-rail-item-count">{row.count}</span>
                                    </button>
                                  );
                                })
                              : null}
                          </div>
                        );
                      })
                    : null}
                </div>
              );
            })}
          </nav>
        </aside>

        <section className="people-panel">
          <div className="people-panel-toolbar">
            <div className="people-panel-chips">
              <span className="people-panel-count">
                {filtered.length} 人
                {data ? ` / 共 ${countVisibleDirectoryItems(data.items)} 人` : ''}
              </span>
            </div>
            <div className="people-panel-controls">
              <Button
                type={hpOnly ? 'primary' : 'default'}
                disabled={!batchId}
                title={batchId ? undefined : '需先在顶部选择批次'}
                onClick={() => setHpOnly((v) => !v)}
              >
                只看高潜
              </Button>
              <Input.Search
                allowClear
                placeholder="搜索姓名 / 工号 / 岗位"
                className="people-panel-search"
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
          </div>

          <Spin spinning={isLoading}>
            {!data?.items.length && !isLoading ? (
              <Empty description="暂无人员数据" className="people-panel-empty" />
            ) : filtered.length === 0 ? (
              <Empty description="当前筛选下暂无人员" className="people-panel-empty" />
            ) : (
              <>
                <ul className="people-list">
                  {paginatedFiltered.map((emp) => {
                    const deptLine = [emp.dept1, emp.dept2].filter(Boolean).join(' · ') || emp.dept;
                    return (
                      <li key={emp.empId}>
                        <button
                          type="button"
                          className="people-row"
                          onClick={() => setSelectedEmp(emp)}
                        >
                          <span className="people-row-name">{emp.name}</span>
                          <span className="people-row-meta">
                            {deptLine}
                            {emp.position ? ` · ${emp.position}` : ''}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <Pagination
                  className="people-panel-pagination"
                  size="small"
                  hideOnSinglePage
                  current={currentPage}
                  pageSize={PAGE_SIZE}
                  total={filtered.length}
                  showTotal={(total) => `共 ${total} 人`}
                  onChange={(page) => setCurrentPage(page)}
                />
              </>
            )}
          </Spin>
        </section>
      </div>
    </div>
  );
}
